import React, { useState, useEffect } from 'react';
import { notify, confirmDialog } from '../ui/notify';
import { apiCall, withToken } from '../config';

const LuggageShippingAdmin = ({ user }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [updating, setUpdating] = useState(false);

  const statusOptions = [
    { value: 'pending', label: 'Ausstehend', color: 'warning' },
    { value: 'processing', label: 'In Bearbeitung', color: 'info' },
    { value: 'shipped', label: 'Versandt', color: 'primary' },
    { value: 'completed', label: 'Abgeschlossen', color: 'success' }
  ];

  // Laden der Versandanfragen
  const loadRequests = async () => {
    try {
      setLoading(true);
      const data = await apiCall('/luggage-shipping-requests');
      setRequests(data);
      
    } catch (error) {
      console.error('Fehler beim Laden der Versandanfragen:', error);
      notify('Fehler beim Laden der Anfragen: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRequests();
  }, []);

  // Status-Update
  const updateRequestStatus = async (requestId, newStatus, notes = '') => {
    try {
      setUpdating(true);
      
      await apiCall(`/luggage-shipping-requests/${requestId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: newStatus, notes })
      });

      // Lokale Daten aktualisieren
      setRequests(prev => 
        prev.map(req => 
          req.id === requestId 
            ? { ...req, status: newStatus, notes, updated_at: new Date().toISOString() }
            : req
        )
      );

      // Wenn ein Request im Modal offen ist, aktualisieren
      if (selectedRequest && selectedRequest.id === requestId) {
        setSelectedRequest(prev => ({ 
          ...prev, 
          status: newStatus, 
          notes, 
          updated_at: new Date().toISOString() 
        }));
      }

      notify('Status erfolgreich aktualisiert!');
      
    } catch (error) {
      console.error('Fehler beim Status-Update:', error);
      notify('Fehler beim Aktualisieren: ' + error.message);
    } finally {
      setUpdating(false);
    }
  };

  // Versandanfrage löschen
  const deleteRequest = async (requestId) => {
    if (!await confirmDialog('Soll diese Versandanfrage wirklich gelöscht werden? Diese Aktion kann nicht rückgängig gemacht werden.')) {
      return;
    }

    try {
      setUpdating(true);
      
      const response = await apiCall(`/luggage-shipping-requests/${requestId}`, {
        method: 'DELETE'
      });

      // Lokale Daten aktualisieren - Request entfernen
      setRequests(prev => prev.filter(req => req.id !== requestId));

      // Modal schließen falls dieser Request geöffnet war
      if (selectedRequest && selectedRequest.id === requestId) {
        setShowModal(false);
        setSelectedRequest(null);
      }

      notify('Versandanfrage erfolgreich gelöscht!');
      
    } catch (error) {
      console.error('Fehler beim Löschen:', error);
      notify('Fehler beim Löschen: ' + error.message);
    } finally {
      setUpdating(false);
    }
  };

  // Foto-URL generieren
  const getPhotoUrl = (photoPath) => {
    if (!photoPath) return null;
    const filename = photoPath.split(/[/\\]/).pop(); // Nur Dateiname extrahieren
    
    // Verwende dynamische API-Base-URL für Production-Kompatibilität
    const baseUrl = process.env.NODE_ENV === 'production' 
      ? window.location.origin
      : 'http://localhost:4000';
    
    return withToken(`${baseUrl}/api/luggage-photos/${filename}`);
  };

  // Formatierung der Daten
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('de-DE');
  };

  const getStatusBadge = (status) => {
    const statusConfig = statusOptions.find(opt => opt.value === status) || 
                        { label: status, color: 'secondary' };
    return (
      <span className={`badge bg-${statusConfig.color}`}>
        {statusConfig.label}
      </span>
    );
  };

  // Gefilterte Anfragen
  const filteredRequests = statusFilter === 'all' 
    ? requests 
    : requests.filter(req => req.status === statusFilter);

  if (loading) {
    return (
      <div className="d-flex justify-content-center p-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Laden...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid p-4">
      <div className="row">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2>
              <i className="bi bi-box-seam text-primary me-2"></i>
              Gepäck-Versandanfragen
            </h2>
            <button 
              className="btn btn-outline-primary"
              onClick={loadRequests}
              disabled={loading}
            >
              <i className="bi bi-arrow-clockwise me-2"></i>
              Aktualisieren
            </button>
          </div>

          {/* Filter */}
          <div className="row mb-4">
            <div className="col-md-6">
              <label className="form-label">Status filtern:</label>
              <select 
                className="form-select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">Alle Anfragen</option>
                {statusOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-6 d-flex align-items-end">
              <div className="text-muted">
                Gesamt: {requests.length} | Gefiltert: {filteredRequests.length}
              </div>
            </div>
          </div>

          {/* Anfragen-Liste */}
          {filteredRequests.length === 0 ? (
            <div className="text-center p-5">
              <i className="bi bi-inbox display-4 text-muted"></i>
              <h4 className="text-muted mt-3">
                {statusFilter === 'all' ? 'Keine Versandanfragen vorhanden' : 'Keine Anfragen mit diesem Status'}
              </h4>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead className="table-dark">
                  <tr>
                    <th>Ticket #</th>
                    <th>Station</th>
                    <th>Kunde</th>
                    <th>E-Mail</th>
                    <th>Status</th>
                    <th>Erstellt</th>
                    <th>Fotos</th>
                    <th>Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRequests.map(request => (
                    <tr key={request.id}>
                      <td>
                        <strong>{request.ticket_number}</strong>
                      </td>
                      <td>
                        <span className="badge bg-secondary">{request.pickup_station}</span>
                      </td>
                      <td>{request.customer_name}</td>
                      <td>
                        <a href={`mailto:${request.customer_email}`}>
                          {request.customer_email}
                        </a>
                      </td>
                      <td>{getStatusBadge(request.status)}</td>
                      <td>{formatDate(request.created_at)}</td>
                      <td>
                        <div className="d-flex gap-1">
                          {request.ticket_photo_path && (
                            <span className="badge bg-info" title="Ticket-Foto vorhanden">
                              <i className="bi bi-image"></i>
                            </span>
                          )}
                          {request.dhl_label_photo_path && (
                            <span className="badge bg-warning" title="DHL-Label vorhanden">
                              <i className="bi bi-tag"></i>
                            </span>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="d-flex gap-2">
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => {
                              setSelectedRequest(request);
                              setShowModal(true);
                            }}
                          >
                            <i className="bi bi-eye me-1"></i>
                            Details
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => deleteRequest(request.id)}
                            disabled={updating}
                            title="Versandanfrage löschen"
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Detail-Modal */}
      {showModal && selectedRequest && (
        <div className="modal fade show" style={{ display: 'block' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-box-seam me-2"></i>
                  Versandanfrage #{selectedRequest.ticket_number}
                </h5>
                <button 
                  type="button" 
                  className="btn-close"
                  onClick={() => setShowModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-6">
                    <h6>Kundendaten</h6>
                    <table className="table table-sm">
                      <tbody>
                        <tr>
                          <td><strong>Name:</strong></td>
                          <td>{selectedRequest.customer_name}</td>
                        </tr>
                        <tr>
                          <td><strong>E-Mail:</strong></td>
                          <td>
                            <a href={`mailto:${selectedRequest.customer_email}`}>
                              {selectedRequest.customer_email}
                            </a>
                          </td>
                        </tr>
                        {selectedRequest.customer_phone && (
                          <tr>
                            <td><strong>Telefon:</strong></td>
                            <td>
                              <a href={`tel:${selectedRequest.customer_phone}`}>
                                {selectedRequest.customer_phone}
                              </a>
                            </td>
                          </tr>
                        )}
                        <tr>
                          <td><strong>Adresse:</strong></td>
                          <td>{selectedRequest.shipping_address}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div className="col-md-6">
                    <h6>Anfrage-Details</h6>
                    <table className="table table-sm">
                      <tbody>
                        <tr>
                          <td><strong>Ticket #:</strong></td>
                          <td>{selectedRequest.ticket_number}</td>
                        </tr>
                        <tr>
                          <td><strong>Station:</strong></td>
                          <td>
                            <span className="badge bg-secondary">
                              {selectedRequest.pickup_station}
                            </span>
                          </td>
                        </tr>
                        <tr>
                          <td><strong>Status:</strong></td>
                          <td>{getStatusBadge(selectedRequest.status)}</td>
                        </tr>
                        <tr>
                          <td><strong>Erstellt:</strong></td>
                          <td>{formatDate(selectedRequest.created_at)}</td>
                        </tr>
                        <tr>
                          <td><strong>Geändert:</strong></td>
                          <td>{formatDate(selectedRequest.updated_at)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Fotos */}
                <div className="row mt-4">
                  <div className="col-12">
                    <h6>Hochgeladene Fotos</h6>
                    <div className="row">
                      {selectedRequest.ticket_photo_path && (
                        <div className="col-md-6 mb-3">
                          <div className="card">
                            <div className="card-header bg-info text-white">
                              <i className="bi bi-image me-2"></i>
                              Ticket-Foto
                            </div>
                            <div className="card-body p-2">
                              <img 
                                src={getPhotoUrl(selectedRequest.ticket_photo_path)}
                                alt="Ticket-Foto"
                                className="img-fluid rounded"
                                style={{ maxHeight: '200px', width: '100%', objectFit: 'contain', cursor: 'pointer' }}
                                onClick={() => window.open(getPhotoUrl(selectedRequest.ticket_photo_path), '_blank')}
                              />
                              <div className="text-center mt-2">
                                <a 
                                  href={getPhotoUrl(selectedRequest.ticket_photo_path)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="btn btn-sm btn-outline-primary"
                                >
                                  <i className="bi bi-zoom-in me-1"></i>
                                  Vergrößern
                                </a>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {selectedRequest.dhl_label_photo_path && (
                        <div className="col-md-6 mb-3">
                          <div className="card">
                            <div className="card-header bg-warning text-dark">
                              <i className="bi bi-tag me-2"></i>
                              DHL-Label
                            </div>
                            <div className="card-body p-2">
                              <img 
                                src={getPhotoUrl(selectedRequest.dhl_label_photo_path)}
                                alt="DHL-Label"
                                className="img-fluid rounded"
                                style={{ maxHeight: '200px', width: '100%', objectFit: 'contain', cursor: 'pointer' }}
                                onClick={() => window.open(getPhotoUrl(selectedRequest.dhl_label_photo_path), '_blank')}
                              />
                              <div className="text-center mt-2">
                                <a 
                                  href={getPhotoUrl(selectedRequest.dhl_label_photo_path)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="btn btn-sm btn-outline-primary"
                                >
                                  <i className="bi bi-zoom-in me-1"></i>
                                  Vergrößern
                                </a>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {!selectedRequest.ticket_photo_path && !selectedRequest.dhl_label_photo_path && (
                      <div className="text-center p-3 text-muted">
                        <i className="bi bi-image-alt display-6"></i>
                        <p>Keine Fotos hochgeladen</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Notizen */}
                {selectedRequest.notes && (
                  <div className="row mt-3">
                    <div className="col-12">
                      <h6>Notizen</h6>
                      <div className="alert alert-info">
                        {selectedRequest.notes}
                      </div>
                    </div>
                  </div>
                )}

                {/* Status-Update */}
                <div className="row mt-4">
                  <div className="col-12">
                    <h6>Status ändern</h6>
                    <div className="d-flex gap-2 flex-wrap">
                      {statusOptions.map(option => (
                        <button
                          key={option.value}
                          className={`btn btn-outline-${option.color} ${
                            selectedRequest.status === option.value ? 'active' : ''
                          }`}
                          onClick={() => updateRequestStatus(selectedRequest.id, option.value)}
                          disabled={updating || selectedRequest.status === option.value}
                        >
                          {updating ? (
                            <span className="spinner-border spinner-border-sm me-1"></span>
                          ) : null}
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary"
                  onClick={() => setShowModal(false)}
                >
                  Schließen
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Modal-Hintergrund */}
      {showModal && (
        <div 
          className="modal-backdrop fade show"
          onClick={() => setShowModal(false)}
        ></div>
      )}
    </div>
  );
};

export default LuggageShippingAdmin;